import { useNavigate } from "react-router";
import type {
  DestinationOptions,
  GetDestinationResponse,
  UpdateDestinationOptions,
} from "@generated/import-openapi.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { DestinationResource, PaginationParams } from "@prequel/import-react";
import { useImportContext } from "@prequel/import-react";

import { importClient } from "./sdk/client";

import { useAllPages } from "@/api/pagination";
import { useToast } from "@/providers/ToastProvider";

export type { DestinationOptions, DestinationResource, UpdateDestinationOptions } from "@generated/import-openapi.gen";

const routes = {
  get: (id: string) => `/import/destinations/${id}`,
  create: "/import/destinations",
  update: (id: string) => `/import/destinations/${id}`,
  delete: (id: string) => `/import/destinations/${id}`,
};

const keys = { all: ["import", "destinations"] as const };

export function useAllDestinations() {
  const { destinations } = useImportContext();

  return useAllPages<DestinationResource>([...keys.all], (params: PaginationParams) => destinations.list(params));
}

export function useDestination(id: string) {
  return useQuery({
    queryKey: [...keys.all, id],
    queryFn: () => importClient.get<GetDestinationResponse>(routes.get(id)).then((data) => data.destination),
  });
}

export function useCreateDestination() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (destination: DestinationOptions) =>
      importClient.post<GetDestinationResponse>(routes.create, { destination }).then((data) => data.destination),
    onSuccess: (destination) => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Destination created successfully!" });
      navigate(`/import/destinations/${destination.id}`);
    },
  });
}

export function useDeleteDestination() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => importClient.delete(routes.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Destination deleted successfully!" });
      navigate("/import/destinations");
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

export function useUpdateDestination() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, destination }: { id: string; destination: UpdateDestinationOptions }) =>
      importClient.patch<GetDestinationResponse>(routes.update(id), { destination }).then((data) => data.destination),
    onSuccess: (destination) => {
      queryClient.setQueryData([...keys.all, destination.id], destination);
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Destination updated successfully!" });
      navigate(`/import/destinations/${destination.id}`);
    },
  });
}
